/* eslint-disable no-console */

import React, { useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import classNames from 'classnames';

import CartFlyout from '../../components/CartFlyout';
import Footer from '../Footer';
import Header from '../Header';
import VerificationModal from '../../components/RegisterAccount/VerificationModal';
import { PageSpinner } from '../../components/Spinner';
import { AuthContext } from '../../context/auth-context';
import { StoreContext } from '../../context/store-context';
import { gidUrlToId, normalize } from '../../utils';

const ns = `layout-default`;

const ShopifyLayout = ({ children, location }) => {
  const { pathname } = location;
  const rootClassnames = useMemo(() => classNames({ [`${ns}`]: true }), []);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isVerificationAlertVisible, setIsVerificationAlertVisible] = useState(false);
  const { state: authState, actions: authActions } = useContext(AuthContext);
  const { isInitialized, isInitializing, auth } = authState;
  const isLoading = isInitializing || !isInitialized;

  const handleRequestClose = useCallback(() => {
    setIsModalOpen(false);
    setIsVerificationAlertVisible(false);
    setIsVerifying(false);
  }, []);

  const handleVerificationAction = useCallback(async () => {
    try {
      setIsVerifying(true);
      setIsVerificationAlertVisible(false);
      const profile = await authActions.getAuth0Profile(true);

      if (!profile.emailVerified) {
        setIsVerificationAlertVisible(true);
      } else {
        setIsModalOpen(false);
      }
    } catch (error) {
      console.error(error.json || error);
    } finally {
      setIsVerifying(false);
    }
  }, [authActions]);

  const handleSignOutAction = useCallback(() => {
    authActions.signOut();
    handleRequestClose();
  }, [authActions, handleRequestClose]);

  const modalMarkup = useMemo(
    () => !pathname.includes('/account/register') && !isLoading && isModalOpen && (
        <VerificationModal
          isOpen={isModalOpen}
          isVerifying={isVerifying}
          isVerificationAlertVisible={isVerificationAlertVisible}
          onVerificationAction={handleVerificationAction}
          onSignOutAction={handleSignOutAction}
          onRequestClose={handleRequestClose}
          modalProps={{
            shouldHideCloseButton: true,
            shouldCloseOnEsc: false,
            shouldCloseOnOverlayClick: false,
          }}
        />
      ),
    [
      pathname,
      isLoading,
      isModalOpen,
      isVerifying,
      isVerificationAlertVisible,
      handleVerificationAction,
      handleSignOutAction,
      handleRequestClose,
    ]
  );

  const emailVerified = useCallback(async () => {
    const isLocalEmailVerified = auth.profile.emailVerified;

    if (!isLocalEmailVerified) {
      const dbProfile = await authActions.getAuth0Profile(true);
      return !!dbProfile.emailVerified;
    }

    return !!isLocalEmailVerified;
  }, [auth.profile.emailVerified, authActions]);

  useEffect(() => {
    (async () => {
      if (auth.isAuthenticated) {
        try {
          const isEmailVerified = await emailVerified();
          if (!isEmailVerified) {
            setIsModalOpen(true);
          } else {
            handleRequestClose();
          }
        } catch (error) {
          console.error(error.json || error);
          handleRequestClose();
        }
      } else {
        handleRequestClose();
      }
    })();
  }, [auth.isAuthenticated, auth.profile.emailVerified, emailVerified, handleRequestClose]);

  const elevarScriptContent = `
    try {
      const settings = {};
      const config = (await import("${process.env.GATSBY_ELEVAR_CONFIG_URL}")).default;
      const scriptUrl = settings.proxyPath
        ? \`\${settings.proxyPath}\${config.script_src_custom_pages_proxied}\`
        : config.script_src_custom_pages;

      if (scriptUrl) {
        const { handler } = await import(scriptUrl);
        await handler(config, settings);
      }
    } catch (error) {
      console.error("Elevar Error:", error);
    }
  `;

  return (
    <>
      <Helmet>
        <script type="module">{elevarScriptContent}</script>
      </Helmet>
      <div className={rootClassnames}>
        <Header pathname={pathname} location={location} />
        <main>
          {isLoading && <PageSpinner />}
          {!isLoading && children}
          {modalMarkup}
        </main>
        <Footer pathname={pathname} location={location} />
      </div>
      <CartFlyout location={location} />
    </>
  );
};

export default ShopifyLayout;
